import { push } from './reduxRouter';
import { compile } from 'path-to-regexp';
import { matchPath } from 'react-router';

import { cache } from 'libs/cache';
import { JsonMap } from 'libs/types';
type PathProps = {
  exact: boolean;
  strict: boolean;
  sensitive: boolean;
};

type PermissionProps<P extends string | number, R extends string | number> = {
  role: R;
  permission: P;
};

type OtherProps = {
  view?: string;
  children?: (BaseRouteModel<any, any, any, any> | BaseRouteModel<never, never, any, any>)[];
  sequence?: number;
  title?: string;
  subtitle?: string;
};

type Config<P extends string | number, R extends string | number> = Partial<
PathProps & OtherProps & PermissionProps<P, R>
>;

const defaultConfig = {
  exact: false,
  strict: false,
  sensitive: false,
  children: [],
  sequence: 1,
};

const routeNames: string[] = [];

export class BaseRouteModel<
  T extends { [key: string]: string | number } = never,
  StateT extends JsonMap = never,
  PermissionT extends string | number = string,
  RoleT extends string | number = string,
> {
  public readonly name: string;
  public readonly title?: string;
  public readonly subtitle?: string;

  public readonly permission?: PermissionT;
  public readonly role?: RoleT;
  protected parent?: BaseRouteModel<any, any, any, any> | BaseRouteModel<never, never, any, any>;
  protected children: (BaseRouteModel<any, any, any, any> | BaseRouteModel<never, never, any, any>)[];

  private path: string;
  private pathProps: PathProps;
  private view?: string;
  // TODO: move creation to route factory
  constructor(name: string, path: string, config: Config<PermissionT, RoleT> = {}) {
    if (routeNames.includes(name)) {
      throw new Error(`Route Creation Error: route with name "${name}" already exists.`);
    }
    // eslint-disable-next-line fp/no-mutating-methods
    routeNames.push(name);

    const finalConfig = {
      ...defaultConfig,
      ...config,
    };
    this.name = name;
    this.path = path;
    const { exact, strict, sensitive } = finalConfig;
    this.pathProps = {
      exact,
      strict,
      sensitive,
    };

    this.role = finalConfig.role;
    this.permission = finalConfig.permission;

    this.title = finalConfig.title;
    this.subtitle = finalConfig.subtitle;
    this.children = finalConfig.children;

    // TODO: move to cache decorator
    this.getUrlFormatter = cache(this.getUrlFormatter.bind(this));
    this.getFullPath = cache(this.getFullPath.bind(this));

    // @ts-ignore
    this.children.forEach((child) => child.setParent(this));
  }

  // Path
  public getFullPath(): string {
    if (this.parent) {
      return `${this.parent.getFullPath()}${this.path ? `/${this.path}` : ''}`;
    }
    return this.path;
  }

  public getFullPathWithOrigin(): string {
    return `${window.location.origin}${this.getFullPath()}`;
  }

  public getPathProps() {
    return {
      ...this.pathProps,
      // Switch treats an empty path as "no path restriction" (path || from), which silently
      // ignores `exact`. Substituting '/' preserves matching behavior (every URL starts with
      // '/') while making `exact` actually take effect for root-level routes.
      path: this.getFullPath() || '/',
    };
  }
  public isSamePath(path: string, view?: string): boolean {
    const arePathesMatched = matchPath(path, { ...this.pathProps, path: this.getFullPath() });
    return arePathesMatched ? !view || view === this.view : false;
  }
  // // Children
  public findChildByPath(pathname: string, view?: string) {
    return this.children.find((route) => route.isSamePath(pathname, view));
  }

  public getUrlFormatter(): (keys?: T) => string {
    // @ts-ignore
    return compile(this.getFullPath(), { encode: encodeURIComponent });
  }
  // : match<{ [key in keyof T]?: string | undefined }> | null
  public getKeysFromPath(path: string) {
    return matchPath(path, this.getPathProps());
  }

  public findChildByName(name: string) {
    return this.children.find((child) => child.name === name);
  }

  // Permissions
  public isPermittedCurrentOnly(..._: any): boolean {
    return true;
  }
  public isPermitted(..._: any): boolean {
    return true;
  }
  public findChildrenByPermission(..._: any) {
    return this.children;
  }

  public getUrl(keys?: T) {
    return this.getUrlFormatter()(keys);
  }

  // TODO: move out
  public open(keys?: T, state?: StateT, config?: { hasPrevious?: boolean; returnTo?: string }) {
    const stateFinal = state ? { ...state, config } : config ? { config } : undefined;
    return push(this.getUrl(keys), stateFinal);
  }

  // returnTo captures the current URL before navigating away, so a nested flow entered from here
  // (however many push/replace hops deep it goes) can later jump straight back to it - see
  // libs/router/actions.ts's goToOrigin.
  public openWithPrevious(keys?: T, state?: StateT) {
    return this.open(keys, state, {
      hasPrevious: true,
      returnTo: `${window.location.pathname}${window.location.search}`,
    });
  }

  // @ts-ignore
  private setParent(parent: BaseRouteModel<T, StateT, PermissionT, RoleT>): void {
    this.parent = parent;
  }
}
